import type { AdminCompanyDetailDto, CompanyFeatureFlag } from '@@types/api.types';
import { CheckboxEditSection } from '@components/shared/checkbox-edit-section.component';
import { Badge } from '@components/ui/badge';
import { errorToast } from '@hooks/toast.hook';
import { useAdminUpdateOrganizationFeatureFlags } from '@queries/admin-organizations.query';
import { COMPANY_FEATURE_FLAG_OPTIONS } from '@utils/constants';
import React from 'react';

type AdminOrganizationFeatureFlagsTileProps = {
  isDeleted: boolean;
  organization: AdminCompanyDetailDto;
  organizationId: string;
};

export const AdminOrganizationFeatureFlagsTile: React.FC<AdminOrganizationFeatureFlagsTileProps> = (props) => {
  const { isDeleted, organization, organizationId } = props;
  const { isPending: isUpdatingFeatureFlags, mutateAsync: updateOrganizationFeatureFlags } =
    useAdminUpdateOrganizationFeatureFlags();

  const handleSaveFeatureFlags = async (featureFlags: CompanyFeatureFlag[]): Promise<void> => {
    try {
      await updateOrganizationFeatureFlags({
        data: {
          featureFlags,
        },
        id: organizationId,
      });
    } catch (error) {
      if (error instanceof Error) {
        errorToast({ description: error.message });
        return;
      }

      errorToast({ description: 'Failed to update feature flags.' });
    }
  };

  return (
    <CheckboxEditSection<CompanyFeatureFlag>
      allowSaveWithoutChanges
      disabled={isDeleted}
      emptyMessage="No feature flags enabled."
      isBusy={isUpdatingFeatureFlags}
      items={(organization.featureFlags ?? []) as CompanyFeatureFlag[]}
      onSave={handleSaveFeatureFlags}
      options={COMPANY_FEATURE_FLAG_OPTIONS.map((option) => ({
        description: option.description,
        label: option.label,
        value: option.value as CompanyFeatureFlag,
      }))}
      renderBadge={(featureFlag) => (
        <Badge
          size="xs"
          variant="secondary"
        >
          {COMPANY_FEATURE_FLAG_OPTIONS.find((option) => option.value === featureFlag)?.label ?? featureFlag}
        </Badge>
      )}
      title="Feature Flags"
      titleTooltip={
        <span>
          Feature flags control <strong>{organization.name}</strong>'s access to premium tools and features.
        </span>
      }
    />
  );
};
